import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { GlobalUtilityService } from '@AppCore/services/global-utility.service';
import { Address } from '@AppRoot/shared/models/user.interface';
import { SharedService } from '@AppRoot/shared/services/shared.service';
import { UserService } from '../user/user.service';
import { APIResponse } from '@AppRoot/shared/models/api-response.interface';
import { ShoppingService } from './shopping.service';
import {
  CartItems,
  CartSummary,
  OrderMetaData,
  User,
} from '@AppRoot/shared/models/common.interface';

import { environment } from '@Environments/environment';
import { Message } from 'primeng/api';
import { take, timeout } from 'rxjs';
import { LocalStorageDataModel } from '@AppCore/constants/local-storage-data-model';

declare var Razorpay: any;

@Component({
  selector: 'app-shopping',
  templateUrl: './shopping.component.html',
  styleUrls: ['./shopping.component.scss'],
})
export class ShoppingComponent implements OnInit, OnDestroy {
  showLoader: boolean = false;
  cartCounts: number = -1;
  paymentSuccess: boolean = false;

  addresses: Array<Address> = [];
  selectedAddressId: number | undefined;
  loading: boolean = false;
  showAddressForm: boolean = false;
  addnewAddress: boolean = false;
  addressToEdit!: Address | null;
  cartSummary!: CartSummary;
  razorpay_order_id: string = '';
  order_id: string = '';
  amount: number = 0;
  user!: User;
  razorpayImg!: string;
  errorBannerMessage: Array<Message> = [];
  bannerMessage: string = '';
  orderPlaced = false;
  randomValue: string = '';
  sameAsBilling: boolean = true;
  paymentMode = 'P';
  buttonText = '';
  updatePaymentSummary = false;
  isUserLoggedin: boolean = false;
  verifyOtp: boolean = false;
  isMobile!: boolean;

  orderMetaData!: OrderMetaData;

  @Output() addressSelected = new EventEmitter<number>();
  @Output() loader = new EventEmitter<boolean>();

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private _globalUtilityService: GlobalUtilityService,
    private _sharedService: SharedService,
    private _userService: UserService,
    private _shoppingService: ShoppingService
  ) {
    this.checkIfMobile();

    // Listen for window resize to update mobile status
    window.addEventListener('resize', () => {
      this.checkIfMobile();
    });
  }

  private checkIfMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  ngOnInit() {
    this.isMobile = this._globalUtilityService.isMobile();
    this.getUserProfile();
    this._sharedService
      .getCartItemCount()
      .pipe(take(1))
      .subscribe({
        next: (data: number) => {
          this.cartCounts = data;
        },
      });
    this.isUserLoggedin = this._globalUtilityService.isUserLoggedIn();
  }

  onSameAsBillingChange() {}

  updateCartSummary(event: {
    cartSummary: CartSummary;
    buttonText: string;
    paymentMode: string;
  }) {
    this.cartSummary = event.cartSummary;
    this.buttonText = event.buttonText;
    this.paymentMode = event.paymentMode;
  }

  navigateBackUrl() {
    this.router.navigate(['/']);
  }

  getUserProfile() {
    if (this._globalUtilityService.isUserLoggedIn()) {
      this.loading = true;
      this._userService.getUserProfile().subscribe({
        next: (res: APIResponse) => {
          this.loading = false;
          this.addresses = res.data.addresses || [];
          if (res.data.addresses && res.data.addresses.length < 1) {
            this.showAddressForm = true;
          }

          const defaultAddress = res.data.addresses.find(
            (a: any) => a.is_default
          );

          if (defaultAddress) {
            this.selectedAddressId = defaultAddress?.id;
          } else if (this.addresses.length > 0) {
            this.selectedAddressId = this.addresses[0]?.id;
          }
        },
        error: (error: any) => {
          this.loading = false;
          this.errorBannerMessage.push({
            severity: 'error',
            summary: error,
            closable: true,
          });
        },
      });
    } else {
      this.loading = false;
      this.showAddressForm = true;
    }
  }

  selectAddress(id: number | undefined) {
    this.selectedAddressId = id;
  }

  updateAddress(address: Address) {
    this.addressToEdit = address;
    this.showAddressForm = true;
  }

  updateAddresses(addresses: Array<Address>) {
    if (this.isUserLoggedin) {
      this.addresses = addresses;
      this.selectedAddressId = this.addresses[0].id;
      this.showAddressForm = false;
    } else {
      this.addresses = addresses;
      this.showAddressForm = false;
    }
  }

  closeForm() {
    this.showAddressForm = false;
  }

  addNewAddress() {
    this.showAddressForm = true;
    this.addressToEdit = null;
  }

  createOrder() {
    this.loading = true;

    if (!this.isUserLoggedin) {
      this.checkoutUserFlow();
      return;
    }

    if (!this.selectedAddressId) {
      this.loading = false;
      this.errorBannerMessage.push({
        severity: 'warn',
        summary: 'Address not selected',
        detail: 'Please select or add an address to proceed.',
        closable: true,
      });
      return;
    }

    this._globalUtilityService.showLoader();
    this._shoppingService
      .checkout(this.selectedAddressId, this.paymentMode)
      .subscribe({
        next: (response: APIResponse) => {
          this.loading = false;
          const data = response.data;

          if (!response.success) {
            this.errorBannerMessage.push({
              severity: 'error',
              closable: false,
              summary: response?.message,
            });
            this.cdr.detectChanges();
            this._globalUtilityService.hideLoader();
            return;
          }

          if (data) {
            this.razorpay_order_id = data.razorpay_order_id;
            this.amount = data.amount;
            this.order_id = data.order_id;
          }

          this.user = data.user;

          this.randomValue = data.randomValue;
          if (this.paymentMode == 'P') {
            this.checkout();
          }

          if (this.paymentMode == 'C') {
            this.orderPlaced = true;
            this.cdr.detectChanges();
            this.paymentSuccess = true;
            this.orderMetaData = response.data.order;
            this._sharedService.clearCart();
            this._globalUtilityService.hideLoader();
          }
        },
        error: (error: any) => {
          this.loading = false;
          this.errorBannerMessage.push({
            severity: 'error',
            summary: error,
            closable: true,
          });
          this._globalUtilityService.hideLoader();
        },
      });
  }

  checkoutUserFlow() {
    this._sharedService.cartItems.pipe(take(1)).subscribe({
      next: (cart: CartItems[]) => {
        const payload = {
          address: this.addresses[0],
          payment_mode: this.paymentMode,
          cart_items: cart,
        };

        this.handleCheckoutUserFlow(payload);
      },
      error: (error: any) => {
        this._globalUtilityService.hideLoader();
        this.errorBannerMessage.push({
          severity: 'error',
          summary: error,
          closable: true,
        });
      },
    });
  }

  handleCheckoutUserFlow(payload: any) {
    this._globalUtilityService.showLoader();
    this._shoppingService.checkoutUserFlow(payload).subscribe({
      next: (response: APIResponse) => {
        this.loading = false;
        if (response.success) {
          if (this.paymentMode == 'P' && response.data.order) {
            const order = response.data.order;
            this.razorpay_order_id = order.razorpay_order_id;
            this.amount = order.amount;
            this.order_id = order.order_id;
            this.randomValue = order.randomValue;
            this.user = response.data.user;

            this._globalUtilityService.setLoginStatus(true);
            this._globalUtilityService.setUserData(response.data.user);
            this._globalUtilityService.setDataOnLocalStorage(
              LocalStorageDataModel.AUTH_KEY,
              response.data.token
            );

            this.initiatePayment();
          }

          if (this.paymentMode == 'C') {
            this.verifyOtp = true;
            this._globalUtilityService.hideLoader();
            this.user = response.data.user;
            this.selectedAddressId = response.data.address_id;
            return;
          }
        } else {
          this.errorBannerMessage.push({
            severity: 'error',
            closable: false,
            summary: response?.message,
          });
          this.cdr.detectChanges();
          this._globalUtilityService.hideLoader();
          return;
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.errorBannerMessage.push({
          severity: 'error',
          summary: error,
          closable: true,
        });
        this._globalUtilityService.hideLoader();
      },
    });
  }

  updateLoader(event: boolean) {
    this.showLoader = event;
  }

  checkout() {
    if (!this._globalUtilityService.isUserLoggedIn()) {
      this.showLoader = false;
      this.router.navigate(['/auth'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    this.initiatePayment();
  }

  initiatePayment() {
    const options = {
      key: this.randomValue,
      amount: this.amount,
      currency: 'INR',
      name: 'Mashhoor Plus Pharmaceuticals Pvt ltd',
      description: 'Sale',
      order_id: this.razorpay_order_id,
      handler: (response: any) => {
        this._globalUtilityService.showLoader();
        let {
          razorpay_payment_id,
          razorpay_order_id,
          razorpay_signature,
          razorpay_invoice_receipt,
          razorpay_invoice_id,
          razorpay_invoice_status,
        } = response;

        this._shoppingService
          .createOrder(
            +this.order_id,
            razorpay_payment_id,
            razorpay_order_id,
            razorpay_signature,
            this.user.user_id,
            razorpay_invoice_receipt,
            razorpay_invoice_id,
            razorpay_invoice_status
          )
          .subscribe({
            next: (response: APIResponse) => {
              if (response.success === true) {
                this.orderPlaced = true;
                this.cdr.detectChanges();
                this.paymentSuccess = true;
                this._sharedService.clearCart();
                this._globalUtilityService.hideLoader();
                this.orderMetaData = response.data.order;
              } else {
                this.paymentSuccess = false;
                this.errorBannerMessage.push({
                  severity: 'error',
                  summary: 'Payment Failed for Order',
                  detail: response.message,
                });

                this.cdr.detectChanges();
                this._globalUtilityService.hideLoader();
              }
            },
            error: (error: any) => {
              this._globalUtilityService.hideLoader();

              this.errorBannerMessage.push({
                severity: 'error',
                summary: 'error',
                detail: error,
              });
            },
          });
      },
      image: 'https://images.mashhoorplus.in/file/mashhoor-plus.png',
      modal: {
        ondismiss: () => {
          console.log('Payment Dismissed');
          this.paymentSuccess = false;
          this._shoppingService
            .cancelOrder(+this.order_id, this.razorpay_order_id)
            .subscribe({
              next: (response: APIResponse) => {
                if (response.success === true) {
                  this._globalUtilityService.hideLoader();

                  this.errorBannerMessage.push({
                    severity: 'warn',
                    summary: 'Order Has been cancelled',
                    detail: 'You closed the payment window. Please try again.',
                  });
                } else {
                  this._globalUtilityService.hideLoader();

                  this.errorBannerMessage.push({
                    severity: 'error',
                    summary: 'Failed to cancel order',
                    detail: response.message,
                  });
                }
                this.cdr.detectChanges();
              },
              error: (error: any) => {
                this.errorBannerMessage.push({
                  severity: 'error',
                  summary: 'error',
                });
              },
            });
        },
        animation: true,
      },
      prefill: {
        name: this.user?.first_name + ' ' + this.user?.last_name,
        email: this.user?.email,
        contact: this.user?.phone,
      },
      timeout: 300,
      notes: {
        address: '227, Rajvansh Vihar Garh Road,Meerut, Uttar Pradesh, 250004',
      },
      theme: {
        color: '#F37254',
      },
      config: {
        display: {
          blocks: {
            upi: {
              name: 'UPI',
              instruments: [
                {
                  method: 'upi',
                },
              ],
            },
            card: {
              name: 'Cards',
              instruments: [
                {
                  method: 'card',
                },
              ],
            },
            netbanking: {
              name: 'Net Banking',
              instruments: [
                {
                  method: 'netbanking',
                },
              ],
            },
          },
          sequence: ['block.upi', 'block.card', 'block.netbanking'],
        },
      },
    };

    const rzp1 = new Razorpay(options);
    this._globalUtilityService.hideLoader();
    rzp1.open();
  }

  handleVerifyOtpResponse(event: any) {
    this._globalUtilityService.showLoader();
    this.verifyOtp = false;
    this._sharedService.cartItems.pipe(take(1)).subscribe({
      next: (cart: CartItems[]) => {
        const payload = {
          address_id: this.selectedAddressId,
          user: event.user,
          token: event.token,
          cart_items: cart,
        };
        this._shoppingService.checkoutUserVerify(payload).subscribe({
          next: (res: APIResponse) => {
            if (res.success) {
              this.orderPlaced = true;
              this.cdr.detectChanges();
              this.paymentSuccess = true;
              this._sharedService.clearCart();
              console.log(res.data.order);

              this.orderMetaData = res.data.order;
            } else {
              this.errorBannerMessage.push({
                severity: 'error',
                summary: 'Order Failed',
              });
            }
            this._globalUtilityService.hideLoader();
          },
          error: () => {
            this._globalUtilityService.hideLoader();
          },
        });
      },
      error: () => {
        this._globalUtilityService.hideLoader();
        this._globalUtilityService.showToast('error', 'Cart Items Missing');
      },
    });
  }

  ngOnDestroy(): void {
    // Clean up event listener
    window.removeEventListener('resize', this.checkIfMobile);
  }
}
